import React, { useMemo } from "react";
import {
  Box,
  Typography,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Drawer,
  Divider,
  Chip,
  alpha,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import { useTranslation } from "react-i18next";
import {
  NAV_SECTIONS,
  filterCatalogByPermissions,
} from "../../constants/navCatalog";
import { getNavIcon } from "./navIcons";
import { dashCatalogPaperSx } from "./myDashboardStyles";

const DRAWER_WIDTH = 320;

export default function WidgetCatalogDrawer({
  open,
  onClose,
  permissions,
  hasProveedorLimit,
  placedCatalogIds = [],
  onAdd,
}) {
  const { t } = useTranslation();

  const available = useMemo(
    () => filterCatalogByPermissions(permissions, { hasProveedorLimit }),
    [permissions, hasProveedorLimit],
  );

  const bySection = useMemo(() => {
    const map = {};
    available.forEach((item) => {
      if (!map[item.section]) map[item.section] = [];
      map[item.section].push(item);
    });
    return map;
  }, [available]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      variant="persistent"
      PaperProps={{ sx: dashCatalogPaperSx }}
    >
      <Box
        sx={(theme) => ({
          p: 2.25,
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 1,
          borderBottom: "1px solid",
          borderColor: alpha(theme.palette.divider, 0.7),
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, transparent 70%)`,
        })}
      >
        <Box>
          <Typography variant="subtitle1" fontWeight={800} letterSpacing={-0.2}>
            {t("myDashboard.catalogTitle")}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t("myDashboard.catalogHint")}
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          aria-label="close"
          sx={(theme) => ({
            border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
            borderRadius: 1.5,
          })}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={{ overflow: "auto", flex: 1, pb: 2 }}>
        {NAV_SECTIONS.map((section) => {
          const items = bySection[section.id];
          if (!items?.length) return null;
          return (
            <Box key={section.id} sx={{ mb: 0.5 }}>
              <Typography
                variant="overline"
                sx={{
                  px: 2.25,
                  pt: 1.75,
                  pb: 0.5,
                  display: "block",
                  color: "text.secondary",
                  fontWeight: 800,
                  letterSpacing: 0.9,
                }}
              >
                {t(section.labelKey)}
              </Typography>
              <List dense disablePadding>
                {items.map((item) => {
                  const Icon = getNavIcon(item.icon);
                  const already =
                    item.type === "data" &&
                    placedCatalogIds.includes(item.id);
                  return (
                    <ListItemButton
                      key={item.id}
                      disabled={already}
                      onClick={() => onAdd(item)}
                      sx={(theme) => ({
                        mx: 1.25,
                        mb: 0.35,
                        borderRadius: 2,
                        border: `1px solid transparent`,
                        transition: "all 0.15s ease",
                        "&:hover": {
                          backgroundColor: alpha(theme.palette.primary.main, 0.07),
                          borderColor: alpha(theme.palette.primary.main, 0.18),
                        },
                      })}
                    >
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <Box
                          sx={(theme) => ({
                            width: 32,
                            height: 32,
                            borderRadius: 1.5,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: theme.palette.primary.main,
                            background:
                              theme.palette.mode === "dark"
                                ? `linear-gradient(145deg, ${alpha(theme.palette.primary.main, 0.28)}, ${alpha("#00bcd4", 0.2)})`
                                : `linear-gradient(145deg, ${alpha(theme.palette.primary.main, 0.14)}, ${alpha("#00bcd4", 0.1)})`,
                            border: `1px solid ${alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.35 : 0.18)}`,
                          })}
                        >
                          <Icon sx={{ fontSize: 18 }} />
                        </Box>
                      </ListItemIcon>
                      <ListItemText
                        primary={t(item.labelKey)}
                        secondary={
                          already
                            ? t("myDashboard.alreadyAdded")
                            : item.type === "data"
                              ? t("myDashboard.dataWidget")
                              : t("myDashboard.shortcut")
                        }
                        primaryTypographyProps={{
                          variant: "body2",
                          fontWeight: 700,
                        }}
                        secondaryTypographyProps={{ variant: "caption" }}
                      />
                      {!already && (
                        <AddRoundedIcon
                          sx={{ fontSize: 18, color: "text.disabled" }}
                        />
                      )}
                      {already && (
                        <Chip
                          size="small"
                          label="OK"
                          sx={{ height: 22, fontSize: "0.65rem", fontWeight: 700 }}
                        />
                      )}
                    </ListItemButton>
                  );
                })}
              </List>
              <Divider sx={{ mx: 2, mt: 0.75, opacity: 0.6 }} />
            </Box>
          );
        })}
      </Box>
    </Drawer>
  );
}

export { DRAWER_WIDTH };
